import type { ApiResponse, Brand, ComparisonSettings } from '@/types/domain';
import type { CsvValidationIssue } from '@/utils/csvImport';

const BASE = 'https://size-match.brendanlowen.workers.dev/api/admin';

async function adminRequest<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  return (await res.json()) as ApiResponse<T>;
}

export interface CsvImportSummary {
  totalRowsInFile: number;
  rowsImported: number;
  chartsTouched: number;
  rowsSkipped: number;
  issues: CsvValidationIssue[];
  note: string;
}

export interface AuditLogRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  previous_value: string | null;
  new_value: string | null;
  user_id: string | null;
  timestamp: string;
}

export interface AdminChartSummary {
  id: string;
  brandId: string;
  brandName: string;
  brandSlug: string;
  audience: string;
  category: string;
  garmentType: string | null;
  unit: string;
  sourceUrl: string | null;
  verificationStatus: string;
  version: number;
  notes: string | null;
  rowCount: number;
}

export const adminApi = {
  login(password: string) {
    return adminRequest<{ loggedIn: boolean }>('/login', { method: 'POST', body: JSON.stringify({ password }) });
  },
  logout() {
    return adminRequest<{ loggedIn: boolean }>('/logout', { method: 'POST' });
  },
  listBrands() {
    return adminRequest<Brand[]>('/brands');
  },
  listCharts() {
    return adminRequest<AdminChartSummary[]>('/charts');
  },
  createBrand(input: { name: string; country?: string; officialWebsite?: string; sourceUrl?: string; notes?: string }) {
    return adminRequest<Brand>('/brands', { method: 'POST', body: JSON.stringify(input) });
  },
  updateBrand(id: string, input: Partial<Brand>) {
    return adminRequest<Brand>(`/brands/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) });
  },
  verifyChart(chartId: string, decision: 'verified' | 'rejected') {
    return adminRequest<{ chartId: string; decision: string }>(`/charts/${encodeURIComponent(chartId)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    });
  },
  deleteChart(chartId: string) {
    return adminRequest<{ deleted: boolean }>(`/charts/${encodeURIComponent(chartId)}`, { method: 'DELETE' });
  },
  importCsv(csvText: string) {
    return adminRequest<CsvImportSummary>('/charts/import', {
      method: 'POST',
      headers: { 'Content-Type': 'text/csv' },
      body: csvText,
    });
  },
  getSettings() {
    return adminRequest<ComparisonSettings>('/settings');
  },
  updateSettings(input: Partial<ComparisonSettings>) {
    return adminRequest<ComparisonSettings>('/settings', { method: 'PUT', body: JSON.stringify(input) });
  },
  getAuditLog(entityType?: string) {
    const qs = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
    return adminRequest<AuditLogRow[]>(`/audit-log${qs}`);
  },
};

export const CSV_TEMPLATE_URL = `${BASE}/charts/import/template`;
